import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authenticateUser } from "api/auth";

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  userInfo: any;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<any | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const storedToken = sessionStorage.getItem("jwt_token");
    const storedUserInfo = sessionStorage.getItem("auth_data");
    if (storedUserInfo) {
      setUserInfo(JSON.parse(storedUserInfo).user);
    }
    if (storedToken) {
      setToken(storedToken);
    }
    setIsAuthLoading(false);
  }, []);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, [token]);

  const login = useCallback(async (email: string, password: string) => {
    setIsAuthLoading(true);
    try {
      const response = await authenticateUser({ email, password });

      if (response?.data?.success && response.data.token) {
        const authData = response.data;
        setUserInfo(authData.user);
        sessionStorage.setItem("auth_data", JSON.stringify(authData));
        sessionStorage.setItem("jwt_token", authData.token);

        setToken(authData.token);
      } else {
        throw new Error(response?.data?.message || "Login failed");
      }
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    } finally {
      setIsAuthLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem("jwt_token");
    sessionStorage.removeItem("auth_data");
    setToken(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        isAuthLoading,
        userInfo,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
